/**
 * The hotel's name and logo for the screens nobody has signed in to yet.
 *
 * Signed in, the session carries the property, and the rail reads it from
 * there. The sign-in page, the password pages and an unpaired tablet have no
 * session, so they ask `GET /branding`, which answers without one. Both names
 * are empty when the installation holds several hotels: before sign-in there
 * is no telling which one a person belongs to, and EREG is shown instead.
 *
 * A small store rather than a react-query key, because the kiosk build has no
 * query client and needs the same answer on its pairing screen.
 */

import { useEffect, useState } from 'react'
import { api } from '@/lib/api'

export interface Branding {
  name: string | null
  logo_url: string | null
}

const NONE: Branding = { name: null, logo_url: null }

let current: Branding | null = null
let inflight: Promise<Branding> | null = null
const listeners = new Set<(branding: Branding) => void>()

function load(): Promise<Branding> {
  // Branding is decoration: any failure - even one thrown before a request is
  // made - shows EREG, never an error screen. Hence the call inside `then`.
  inflight ??= Promise.resolve()
    .then(() => api.get<Branding>('/branding'))
    .catch(() => NONE)
    .then((branding) => {
      current = branding
      listeners.forEach((listener) => listener(branding))
      return branding
    })
    .finally(() => {
      inflight = null
    })
  return inflight
}

/** Ask again: the name or logo was just changed in Settings. */
export function refreshBranding(): void {
  void load()
}

/** `undefined` until the first answer, so a screen can wait rather than flash EREG. */
export function useBranding(): Branding | undefined {
  const [branding, setBranding] = useState<Branding | undefined>(current ?? undefined)
  useEffect(() => {
    listeners.add(setBranding)
    if (current) setBranding(current)
    else void load()
    return () => {
      listeners.delete(setBranding)
    }
  }, [])
  return branding
}

/** The browser tab says which hotel this is, not which product. */
export function useDocumentTitle(name: string | null | undefined): void {
  useEffect(() => {
    if (name === undefined) return
    document.title = name || 'EREG'
  }, [name])
}
